import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Logger } from '@mode/shared/contract-common';
import { INITIATOR_CONTEXT, serializeAndStripIncludes } from '@mode/shared/util-halogen';
import * as angular from 'angular';
import { timeout } from 'rxjs/operators';
import { UrlExpanderService } from '../url-expander/url-expander.service';
import { ApiError } from './api.service';
import { UriTemplateService } from '@mode/shared/util-halogen';

/**
 * For some reason, this when this ModeForm is a standalone class, it looses it's thisiness sometimes
 * $http in particular was throwing error when converting a request to a promise. There's some Javascript weirdness
 * that I don't understand, but capturing the $http in this closure seems to work. This matches what was happening before
 * my most recent changes.
 *
 * Specifically, saving IDE preferences broke.
 */
const createModeForm = (
  resourceType,
  representation,
  $http: HttpClient,
  urlExpander: UrlExpanderService,
  $log: Logger,
  uriTemplateService: UriTemplateService
) => {
  /**
   * @class ModeForm An application/vnd.mode+json client for AngularJS
   * @constructor
   * @param {Object} [properties]  Initialize the record with these property values.
   * @param {Object} [options]
   */
  class ModeForm {
    resourceType: any;
    method: string;
    content_type: string;
    action: string;

    constructor(resourceType, representation) {
      // eslint-disable-next-line prefer-spread, prefer-rest-params
      this.$initialize(resourceType, representation);
    }

    /**
     * Constructor logic
     * (which is called by the autogenerated constructor via ModeForm.extend)
     * @param {Object} [representation]  Initialize the resource with this representation.
     * @param {Object} [resourceType]
     */
    $initialize(resourceType, representation) {
      this.resourceType = resourceType;

      angular.forEach(representation, (value, prop) => {
        if (prop === 'input') {
          const inputs = {};
          angular.forEach(value, (input, name) => {
            inputs[name] = input;
          });
          this['_inputs'] = inputs;
        } else {
          this[prop] = value;
        }
      });
    }

    inputs(inputs, name) {
      const segments = name.split('.');

      let input = inputs[segments[0]];
      for (let i = 1; i < segments.length; i++) {
        if (input == null) {
          return null;
        } else {
          input = input[segments[i]];
        }
      }

      return input;
    }

    $submit(params, urlParams, timeoutMs) {
      const deferred = new Promise((resolve, reject) => {
        const submitInputs = angular.copy(this['_inputs']);
        let currentInput;
        const passedParams = angular.copy(params);
        const includes = serializeAndStripIncludes(passedParams);

        angular.forEach(passedParams, (value, name) => {
          currentInput = this.inputs(submitInputs, name);
          if (angular.isDefined(currentInput) && currentInput !== null) {
            if (value !== null) {
              currentInput.value = value;
            }
          } else {
            $log.debug('Could not find input: ', name);
          }
        });

        let url = '';
        let request: Promise<any> = null;
        let data = {};

        if (this.method === 'get') {
          url = this.$expand(this.$serialize(submitInputs));
          request = $http
            .request('get', url, {
              params: includes,
            })
            .toPromise();
        } else {
          const headers = {};

          if (this.content_type) {
            headers['Content-Type'] = this.content_type;
          }

          url = this.$expand(urlParams);
          data = angular.extend(data, this.$serialize(submitInputs));

          request = $http
            .request(this.method, url, {
              body: data,
              headers,
              params: includes,
              context: new HttpContext().set(INITIATOR_CONTEXT, new Error()),
            })
            .pipe(timeout(timeoutMs ?? 100000))
            .toPromise();
        }

        $log.debug('Submitting', this.method, url, data);

        const klass = this.resourceType;
        request.then(
          (response) => {
            resolve(new klass(response || {}));
          },
          (failedResponse) => {
            if (failedResponse instanceof HttpErrorResponse) {
              // Not going to bubble up abort errors since we don't have any reason to handle them
              if (failedResponse.status !== -1) {
                reject(new ApiError(failedResponse));
              }
            } else {
              reject(failedResponse);
            }
          }
        );
      });

      return deferred;
    }

    /**
     * Generate the url for the $save, $fetch and $destroy methods.
     */
    $expand(params) {
      return uriTemplateService.parse(urlExpander.expand(this.action)).expand(params);
    }

    $serialize(inputs?) {
      const data = {};
      const selectedInputs = inputs ? inputs : this['_inputs'];
      const insert = (input, value, path) => {
        let last = data;
        for (let i = 0; i < path.length; i++) {
          if (i + 1 < path.length) {
            if (last[path[i]] == null) {
              last[path[i]] = {};
            }

            last = last[path[i]];
          } else {
            if (
              typeof value === 'boolean' ||
              typeof value === 'number' ||
              (angular.isArray(value) && value.length) ||
              (!angular.isArray(value) && value)
            ) {
              last[path[i]] = value;
            } else if (input.type === 'text') {
              last[path[i]] = '';
            }
          }
        }
      };

      const search = (input, path) => {
        angular.forEach(input, (value, name) => {
          if (name === 'value' && (!angular.isObject(value) || angular.isArray(value))) {
            const val = typeof value === 'boolean' || typeof value === 'number' ? value : value || '';
            insert(input, val, path);
          } else if (angular.isObject(value)) {
            path.push(name);
            search(value, path);
            path.pop();
          }
        });
      };

      angular.forEach(selectedInputs, (input, name) => {
        const path = [];
        if (angular.isDefined(input)) {
          path.push(name);
          search(input, path);
          path.pop();
        }
      });

      return data;
    }
  }
  return new ModeForm(resourceType, representation);
};

@Injectable({ providedIn: 'root' })
export class ModeFormFactoryService {
  constructor(
    private $http: HttpClient,
    private urlExpander: UrlExpanderService,
    private $log: Logger,
    private uriTemplateService: UriTemplateService
  ) {}

  create(resourceType, representation) {
    return createModeForm(
      resourceType,
      representation,
      this.$http,
      this.urlExpander,
      this.$log,
      this.uriTemplateService
    );
  }
}
